/**
 * Visual Editor Service
 * Handles communication with the Claude Agent visual editing API
 */

import axios from "axios";

import { auth } from "../firebase";
import type { ElementData, BindValue } from "@/types/preview-message.types";
import type { LayoutChild } from "@/types/property-panel.types";
import type {
  GetPagesResponse,
  CreatePageResponse,
  DeletePageResponse,
  RenamePageResponse,
  DuplicatePageResponse,
  SetAsHomepageResponse,
} from "@/types/pages.types";
import { getActiveWorkspaceId } from "@/stores/workspace-store";

/**
 * Extract a human-readable error message from an Axios error response.
 */
function extractApiError(error: unknown, fallback: string): string {
  const axiosError = error as {
    response?: { data?: { message?: string; error?: string } };
  };
  return (
    axiosError?.response?.data?.message ||
    axiosError?.response?.data?.error ||
    (error instanceof Error ? error.message : fallback)
  );
}

export interface VisualEditRequest {
  command: "update-text" | "update-style" | "get-element-info";
  bind?: BindValue;
  elementPath: string;
  text?: string;
  styleUpdates?: Record<string, string>;
  source?: {
    fileName: string;
    lineNumber: number;
    columnNumber: number;
  };
  devContainerId: string;
}

export interface BatchVisualEditChange {
  command:
    | "update-text"
    | "update-style"
    | "update-attributes"
    | "add-element"
    | "remove-element"
    | "update-position";

  // Common fields
  elementPath?: string;
  bind?: BindValue;
  source?: {
    fileName: string;
    lineNumber: number;
    columnNumber: number;
  };

  // update-text fields
  text?: string;

  // update-style fields
  styleUpdates?: Record<string, string>;
  classUpdates?: string;

  // update-attributes fields
  attributeUpdates?: Record<string, string>;

  // add-element fields
  parentElementPath?: string;
  elementType?: string;
  classNames?: string;
  attributes?: Record<string, string>;
  children?: LayoutChild[];
  targetSource?: {
    fileName: string;
    lineNumber: number;
    columnNumber: number;
  };
  position?: "top" | "bottom";

  // Pre-built JSX content for section/navigation templates
  rawJsx?: string;
}

export interface BatchVisualEditRequest {
  changes: BatchVisualEditChange[];
}

export interface BatchVisualEditResponse extends VisualEditResponse {
  results?: Array<{
    success: boolean;
    filesModified?: string[];
    changes?: string[];
    error?: string;
  }>;
  slideThumbnailsUpdated?: boolean;
}

export interface VisualEditResponse {
  success: boolean;
  filesModified?: string[];
  changes?: string[];
  error?: string;
  newElementSource?: {
    fileName: string;
    lineNumber: number;
    columnNumber: number;
  };
}

export class VisualEditorService {
  private client;

  constructor(baseUrl?: string) {
    // Use full API URL in production, relative in development
    const apiUrl = import.meta.env.VITE_API_URL || "";
    const fullBaseUrl =
      baseUrl || (apiUrl ? `${apiUrl}/api/claude-agent` : "/api/claude-agent");

    // Create axios instance with auth interceptor
    this.client = axios.create({
      baseURL: fullBaseUrl,
    });

    // Add auth + workspace interceptor
    this.client.interceptors.request.use(async (config) => {
      const token = await auth.getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      // Add workspace context from store
      const workspaceId = getActiveWorkspaceId();
      if (workspaceId) {
        config.headers["X-Workspace-Id"] = workspaceId;
      }
      return config;
    });
  }

  /**
   * Update text content of an element
   */
  async updateText(
    element: ElementData,
    newText: string,
    devContainerId: string,
  ): Promise<VisualEditResponse> {
    try {
      const response = await this.client.post<VisualEditResponse>(
        "/visual-edit",
        {
          command: "update-text",
          elementPath: element.path,
          bind: element.bind,
          text: newText,
          source: element.source,
          devContainerId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update text:", error);
      return {
        success: false,
        error: error instanceof Error ? error.message : "Failed to update text",
      };
    }
  }

  /**
   * Update styles of an element
   */
  async updateStyles(
    element: ElementData,
    styleUpdates: Record<string, string>,
    devContainerId: string,
    classUpdates?: string,
  ): Promise<VisualEditResponse> {
    try {
      const response = await this.client.post<VisualEditResponse>(
        "/visual-edit",
        {
          command: "update-style",
          elementPath: element.path,
          bind: element.bind,
          styleUpdates,
          classUpdates,
          source: element.source,
          devContainerId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to update styles:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to update styles",
      };
    }
  }

  /**
   * Get element information
   */
  async getElementInfo(
    element: ElementData,
    devContainerId: string,
  ): Promise<VisualEditResponse> {
    try {
      const response = await this.client.post<VisualEditResponse>(
        "/get-element-info",
        {
          elementPath: element.path,
          sourceFile: element.source?.fileName,
          devContainerId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to get element info:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to get element info",
      };
    }
  }

  async addElement(
    elementInfo: ElementData,
    parentContainerId: string,
    devContainerId: string,
  ): Promise<VisualEditResponse> {
    try {
      const response = await this.client.post<VisualEditResponse>(
        "/add-element",
        {
          elementInfo: {
            tagName: elementInfo.tagName,
            content: elementInfo.content,
            text: elementInfo.text,
            styles: elementInfo.styles,
            attributes: elementInfo.attributes,
            className: elementInfo.className,
            children: elementInfo.children,
          },
          parentContainerId,
          source: elementInfo.source,
          devContainerId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to add new element:", error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : "Failed to add new element",
      };
    }
  }

  /**
   * Batch update multiple properties
   */
  async batchUpdate(
    element: ElementData,
    updates: {
      text?: string;
      styles?: Record<string, string>;
      attributes?: Record<string, string>;
    },
    devContainerId: string,
  ): Promise<VisualEditResponse> {
    const results: VisualEditResponse[] = [];
    // Update text if provided
    if (updates.text !== undefined) {
      const textResult = await this.updateText(
        element,
        updates.text,
        devContainerId,
      );
      results.push(textResult);
      if (!textResult.success) {
        return textResult;
      }
    }

    // Update styles if provided
    if (updates.styles && Object.keys(updates.styles).length > 0) {
      const styleResult = await this.updateStyles(
        element,
        updates.styles,
        devContainerId,
        updates.attributes?.class,
      );
      results.push(styleResult);
      if (!styleResult.success) {
        return styleResult;
      }
    }

    // Combine results
    const filesModified = new Set<string>();
    const changes: string[] = [];

    results.forEach((result) => {
      if (result.filesModified) {
        result.filesModified.forEach((file) => filesModified.add(file));
      }
      if (result.changes) {
        changes.push(...result.changes);
      }
    });

    return {
      success: true,
      filesModified: Array.from(filesModified),
      changes,
    };
  }

  /**
   * Batch update multiple elements with different changes
   * Supports: update-text, update-style, add-element, remove-element, update-position, reorder-element
   */
  async batchVisualEdit(
    changes: BatchVisualEditChange[],
    devContainerId: string,
  ): Promise<BatchVisualEditResponse> {
    try {
      const response = await this.client.post<BatchVisualEditResponse>(
        "/visual-edit-batch",
        {
          changes,
          devContainerId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Failed to batch update elements:", error);
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : "Failed to batch update elements",
      };
    }
  }
  /**
   * Get all pages in the project
   */
  async getPages(devContainerId: string): Promise<GetPagesResponse> {
    try {
      const response = await this.client.get<GetPagesResponse>("/pages", {
        params: { devContainerId },
      });
      return response.data;
    } catch (error) {
      console.error("Failed to get pages:", error);
      return {
        success: false,
        pages: [],
        pageCount: 0,
      };
    }
  }

  /**
   * Create a new page
   */
  async createPage(routeName: string, devContainerId: string): Promise<CreatePageResponse> {
    try {
      const response = await this.client.post<CreatePageResponse>("/pages", {
        routeName,
        devContainerId,
      });
      return response.data;
    } catch (error: unknown) {
      console.error("Failed to create page:", error);
      return {
        success: false,
        route: "",
        filePath: "",
        registered: false,
        error: extractApiError(error, "Failed to create page"),
      };
    }
  }

  /**
   * Delete a page
   */
  async deletePage(route: string, devContainerId: string): Promise<DeletePageResponse> {
    try {
      const response = await this.client.delete<DeletePageResponse>("/pages", {
        data: { route, devContainerId },
      });
      return response.data;
    } catch (error: unknown) {
      console.error("Failed to delete page:", error);
      return {
        success: false,
        error: extractApiError(error, "Failed to delete page"),
      };
    }
  }

  /**
   * Rename a page
   */
  async renamePage(
    oldRoute: string,
    newRouteName: string,
    devContainerId: string,
  ): Promise<RenamePageResponse> {
    try {
      const response = await this.client.patch<RenamePageResponse>("/pages", {
        oldRoute,
        newRouteName,
        devContainerId,
      });
      return response.data;
    } catch (error: unknown) {
      console.error("Failed to rename page:", error);
      return {
        success: false,
        error: extractApiError(error, "Failed to rename page"),
      };
    }
  }

  /**
   * Set a page as the homepage
   */
  async setAsHomepage(route: string, devContainerId: string): Promise<SetAsHomepageResponse> {
    try {
      const response = await this.client.put<SetAsHomepageResponse>(
        "/pages/set-homepage",
        {
          route,
          devContainerId,
        },
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Failed to set as homepage:", error);
      return {
        success: false,
        error: extractApiError(error, "Failed to set as homepage"),
      };
    }
  }

  /**
   * Duplicate a page
   */
  async duplicatePage(sourceRoute: string, devContainerId: string): Promise<DuplicatePageResponse> {
    try {
      const response = await this.client.put<DuplicatePageResponse>(
        "/pages/duplicate",
        {
          sourceRoute,
          devContainerId,
        },
      );
      return response.data;
    } catch (error: unknown) {
      console.error("Failed to duplicate page:", error);
      return {
        success: false,
        route: "",
        filePath: "",
        registered: false,
        error: extractApiError(error, "Failed to duplicate page"),
      };
    }
  }
}

// Export singleton instance
export const visualEditorService = new VisualEditorService();
